/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * pickerDebug — operator-visible iPhone picker handoff diagnostics.
 *
 * P0 (May 20, 2026 — production retest failed):
 *   The May 20 picker-beacon fix passed Playwright simulation but
 *   the real iPhone production retest still fails. The operator
 *   cannot use DevTools on a phone, so the next layer of evidence
 *   has to be visible ON-DEVICE.
 *
 * This module provides three things:
 *
 *   1. PICKER_FIX_VERSION — a hard-coded version string. The
 *      visible debug panel surfaces this so the operator can
 *      confirm at a glance whether the production bundle actually
 *      contains the latest fix. If they see an older version, the
 *      deploy didn't land OR an old service-worker bundle is
 *      cached.
 *
 *   2. emitPickerEvent / subscribePickerEvents — a tiny in-memory
 *      event bus that every upload entry point pushes lifecycle
 *      events into. The visible debug panel renders the last 30
 *      events with timestamps. The console.warn anchor remains so
 *      Safari DevTools (if the operator chooses) sees them too.
 *
 *   3. isPickerDebugEnabled — single source of truth for whether
 *      to mount the visible panel. Enabled via:
 *        • localStorage["debug:picker"] = "1"   OR
 *        • URL query param ?debugPicker=1 (persists into localStorage
 *          so subsequent navigations keep it on; ?debugPicker=0
 *          clears it).
 *
 * Vocabulary (must stay in lockstep with usePickerHandoff):
 *
 *   handoff_attempt        — fileInputRef.click() about to fire.
 *   handoff_beacon_written — sessionStorage beacon written.
 *   handoff_onchange       — input.onChange fired (file_count, sizes).
 *   handoff_empty          — onChange fired with zero files (cancel
 *                            or iCloud-only edge).
 *   handoff_files_staged   — local component placed files into its
 *                            staged state.
 *   handoff_missed         — parent surface remounted/refocused but
 *                            no onChange ever fired and no files
 *                            staged — the failure mode the operator
 *                            keeps seeing on real iPhones.
 *   handoff_recovered      — basket-style auto-reopen succeeded
 *                            (server-backed paths only).
 *   modal_mount            — upload modal mounted.
 *   modal_unmount          — upload modal unmounted (rare on a
 *                            healthy mount; expected during a real
 *                            WebKit suspend/evict).
 */

// Version stamp — bump this on every change touching the picker
// path so the operator can verify on the visible panel that they
// are testing the freshly-deployed bundle.
export const PICKER_FIX_VERSION = "v5.0-2026-05-21-UPLOAD-BASKET-V2";

export const PICKER_DEBUG_FLAG_KEY = "debug:picker";
export const PICKER_DEBUG_QUERY_PARAM = "debugPicker";

const EVENT_VOCAB = Object.freeze([
  "handoff_attempt",
  "handoff_beacon_written",
  "handoff_onchange",
  "handoff_empty",
  "handoff_files_staged",
  "handoff_missed",
  "handoff_recovered",
  "modal_mount",
  "modal_unmount",
  // P0 May 21, 2026 — durability repair (Round 4) marker. Emitted
  // immediately before the eager `await uploadOne(...)` fires inside
  // onPickFiles. Proves on-device that the new structural fix is
  // actually executing — distinct from `handoff_files_staged` which
  // only proves React state was committed.
  "eager_upload_started",
  "eager_upload_init_ok",
  "eager_upload_init_failed",
  // P0 May 21, 2026 — Upload Basket v2 lifecycle markers.
  "v2_picker_returned",
  "v2_local_rows_created",
  "v2_draft_saved",
  "v2_backend_session_created",
  "v2_upload_started",
  "v2_upload_completed",
  "v2_review_created",
  "v2_hydration_blocked_or_merged",
  "v2_remount",
]);

const listeners = new Set();
const ringBuffer = [];
const RING_LIMIT = 60;

export function emitPickerEvent(type, payload = {}) {
  if (!EVENT_VOCAB.includes(type)) {
    // Typo guard — drop silently so a typo can't pollute the rail.
    return;
  }
  const evt = {
    type,
    payload,
    at: Date.now(),
    iso: new Date().toISOString(),
  };
  ringBuffer.push(evt);
  if (ringBuffer.length > RING_LIMIT) ringBuffer.shift();
  try {
    // eslint-disable-next-line no-console
    console.warn(`[picker:${type}]`, payload);
  } catch (_) { /* noop */ }
  for (const fn of Array.from(listeners)) {
    try { fn(evt, ringBuffer); } catch (_) { /* noop */ }
  }
}

export function subscribePickerEvents(fn) {
  listeners.add(fn);
  // Replay the existing ring so a late mounter sees history.
  try { fn(null, ringBuffer); } catch (_) { /* noop */ }
  return () => listeners.delete(fn);
}

export function getPickerEventRing() {
  return ringBuffer.slice();
}

/**
 * isPickerDebugEnabled — checks localStorage. Side-effect: if the
 * URL contains ?debugPicker=1 or =0, this function toggles the
 * stored flag accordingly. Call once at app boot from a top-level
 * effect.
 */
export function syncPickerDebugFlagFromUrl() {
  try {
    if (typeof window === "undefined" || !window.location) return;
    const url = new URL(window.location.href);
    const v = url.searchParams.get(PICKER_DEBUG_QUERY_PARAM);
    if (v === "1") {
      localStorage.setItem(PICKER_DEBUG_FLAG_KEY, "1");
    } else if (v === "0") {
      localStorage.removeItem(PICKER_DEBUG_FLAG_KEY);
    }
  } catch (_) { /* noop */ }
}

export function isPickerDebugEnabled() {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(PICKER_DEBUG_FLAG_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export const _INTERNAL = { EVENT_VOCAB };
